import type { Facing } from '@/sprites/katie';

/**
 * The dodge roll.
 *
 * A burst of speed with invulnerability for as long as it lasts, then a
 * cooldown. The cooldown is the whole design: without it, dodging is simply a
 * faster way to walk and every fight is won by holding the button. With it,
 * the roll is a decision about when.
 *
 * Invulnerability covers the entire roll rather than a window inside it.
 * Precise timing is a fine thing to ask of a controller and a poor thing to
 * ask of a thumb on glass.
 */

export const DODGE_SECONDS = 0.24;
export const DODGE_COOLDOWN = 0.55;
export const DODGE_SPEED = 165;

const DIRECTIONS: Readonly<Record<Facing, { x: number; y: number }>> = {
  down: { x: 0, y: 1 },
  up: { x: 0, y: -1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

export class Dodge {
  private remaining = 0;
  private cooldown = 0;
  private dirX = 0;
  private dirY = 0;

  /**
   * Roll the way she is moving, or the way she faces when standing still.
   * Rolling on the spot would be a free invulnerability with no commitment.
   */
  start(moveX: number, moveY: number, facing: Facing): boolean {
    if (this.remaining > 0 || this.cooldown > 0) return false;

    const length = Math.hypot(moveX, moveY);
    if (length > 0.01) {
      this.dirX = moveX / length;
      this.dirY = moveY / length;
    } else {
      const direction = DIRECTIONS[facing];
      this.dirX = direction.x;
      this.dirY = direction.y;
    }

    this.remaining = DODGE_SECONDS;
    this.cooldown = DODGE_SECONDS + DODGE_COOLDOWN;
    return true;
  }

  update(dt: number): void {
    this.remaining = Math.max(0, this.remaining - dt);
    this.cooldown = Math.max(0, this.cooldown - dt);
  }

  get isDodging(): boolean {
    return this.remaining > 0;
  }

  get isReady(): boolean {
    return this.cooldown <= 0;
  }

  /** Distance to move this step, in pixels. */
  step(dt: number): { x: number; y: number } {
    if (!this.isDodging) return { x: 0, y: 0 };
    // Eases off across the roll, so it ends by settling rather than stopping.
    const eased = 0.4 + 0.6 * (this.remaining / DODGE_SECONDS);
    return { x: this.dirX * DODGE_SPEED * eased * dt, y: this.dirY * DODGE_SPEED * eased * dt };
  }
}
